import * as React from 'react';
import { Icon, IIconProps } from 'office-ui-fabric-react/lib/Icon';
import { IComponentAs } from '@uifabric/utilities/lib/IComponentAs';
import { getFileTypeIconProps, FileIconType, IFileTypeIconOptions } from '@uifabric/file-type-icons/lib/index';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { fileNameFacet, folderFacet } from '../../facets/ItemFacets';
import { fileIconTypeFacet, oneNoteFacet } from '../../facets/DocumentFacets';
import { itemFileTypeIconControl } from '../../controls/itemFileTypeIconControl';
import { getBrandTypeIconProps } from '../brandIcon/brandIconHelper';
import { DeferredRender } from '../../utilities/components/DeferredRender';
import { getFieldRenderBarrier } from '../fields/FieldHelper';
import { IComponentAsProps } from '@uifabric/utilities/lib/IComponentAs';
import { css } from '@uifabric/utilities/lib/css';
import * as ItemFileTypeIconStyles from './ItemFileTypeIcon.scss';

const ONENOTE_EXTENSION = 'one';

const ICON_SIZE_24 = 24;

/**
 * @internal
 */
export type IFileTypeIconProps = {
  itemKey?: string;
  className?: string;
  isOneNote?: boolean;
  isFolder?: boolean;
  iconAs?: IComponentAs<IIconProps>;
  preferAppIcon?: boolean;
  pageLoadBarrier?: Promise<void>;
} & Pick<IFileTypeIconOptions, 'extension' | 'type'> & {
  size?: IFileTypeIconOptions['size'] | 24;
};

/**
 * @internal
 */
export const FileTypeIcon: React.StatelessComponent<IFileTypeIconProps> = (props: IFileTypeIconProps) => {
  const {
    extension,
    type,
    className,
    isOneNote,
    size: inputSize,
    isFolder,
    preferAppIcon = false,
    pageLoadBarrier
  } = props;

  const size = inputSize === ICON_SIZE_24 ? 20 : inputSize;

  const IconAs: IComponentAs<IIconProps> = props.iconAs || Icon;
  const ext = isOneNote ? ONENOTE_EXTENSION : extension;

  return (
    <DeferredRender barrier={ pageLoadBarrier }>
      <IconAs
        defaultRender={ Icon }
        className={ css(ItemFileTypeIconStyles.root, className, {
          [ItemFileTypeIconStyles.inset24]: inputSize === ICON_SIZE_24
        }) }
        { ...preferAppIcon && ext ? getBrandTypeIconProps({
          size: size,
          extension: ext
        }) : getFileTypeIconProps({
          ...(extension || type || isOneNote || isFolder ? {
            extension: ext,
            type: type !== undefined ? type :
              (isFolder ? 2 as FileIconType.folder : undefined)
          } : {
              type: 3 as FileIconType.genericFile
            }),
          size
        }) }
      />
    </DeferredRender>
  );
};

/**
 * @beta
 */
export interface IItemFileTypeIconProps extends IFileTypeIconProps {
  itemKey: string | undefined;
}

/**
 * @beta
 */
export const ItemFileTypeIcon: React.ComponentType<IItemFileTypeIconProps> = connectToItemCache((
  itemCache: IItemCache,
  props: IItemFileTypeIconProps
): Pick<IFileTypeIconProps, 'extension' | 'type' | 'isOneNote' | 'isFolder' | 'iconAs' | 'pageLoadBarrier'> &
  Pick<IComponentAsProps<IFileTypeIconProps>, 'defaultRender'> => {
  const {
    itemKey
  } = props;

  const {
    fileName,
    folder,
    fileIconType,
    oneNote,
    controlHandler
  } = itemCache.demandItem({
    fileName: fileNameFacet,
    folder: folderFacet,
    fileIconType: fileIconTypeFacet,
    oneNote: oneNoteFacet,
    controlHandler: itemFileTypeIconControl
  }, itemKey);

  const extension = fileName && fileName.extension;

  const {
    iconAs,
    fileTypeIconAs
  } = itemFileTypeIconControl.evaluate(controlHandler)(itemCache, {
    itemKey,
    iconAs: Icon,
    fileTypeIconAs: FileTypeIcon
  });

  return {
    extension,
    defaultRender: fileTypeIconAs,
    isFolder: !!folder,
    type: fileIconType,
    isOneNote: !!oneNote,
    iconAs,
    pageLoadBarrier: getFieldRenderBarrier(itemCache)
  };
})((props: IComponentAsProps<IFileTypeIconProps>) => {
  const {
    defaultRender: DefaultRender,
    ...fileTypeIconProps
  } = props;

  return DefaultRender ? (
    <DefaultRender { ...fileTypeIconProps } />
  ) : null;
});
